import { isJuvenileName } from '@/pages/CFS/reports/cfsReport/mappers';
import {
  cfsReportSections,
  cfsTopSectionFields,
} from '@/pages/CFS/reports/cfsReport/template';
import { CFSReportContext } from '@/pages/CFS/reports/cfsReport/types';

export type CFSFieldKey = string;
export type CFSSectionKey = string;

export interface CFSItemRedactions {
  redactedKeys: Set<CFSFieldKey>;
  redactedWords: Set<string>;
  // Per-occurrence id: `${fieldKey}:${start}:${end}:${wordLower}`
  redactedOccurrences: Set<string>;
}

export type CFSRedactionsMap = Record<
  CFSSectionKey,
  Record<number, CFSItemRedactions>
>;

// Default field keys redacted for any person under 18 in the NAMES section.
export const JUVENILE_DEFAULT_REDACTED_KEYS: readonly CFSFieldKey[] = [
  'name',
  'role',
  'sex',
  'age',
  'race',
  'dob',
  'personType',
  'address',
  'cityStateZip',
  'phoneHome',
  'phoneMobile',
  'phoneWork',
  'phoneCaller',
  'phoneOther',
];

const makeEmptyItem = (): CFSItemRedactions => ({
  redactedKeys: new Set<CFSFieldKey>(),
  redactedWords: new Set<string>(),
  redactedOccurrences: new Set<string>(),
});

const makeJuvenileItem = (): CFSItemRedactions => ({
  redactedKeys: new Set<CFSFieldKey>(JUVENILE_DEFAULT_REDACTED_KEYS),
  redactedWords: new Set<string>(),
  redactedOccurrences: new Set<string>(),
});

export const initCFSRedactions = (ctx: CFSReportContext): CFSRedactionsMap => {
  const redactionsMap: CFSRedactionsMap = {};

  // Top section is single-detail; always one entry at index 0.
  redactionsMap[cfsTopSectionFields.key] = { 0: makeEmptyItem() };

  for (const section of cfsReportSections) {
    if (section.items) {
      const items = section.items(ctx) || [];
      const sectionMap: Record<number, CFSItemRedactions> = {};
      // Always include index 0 so empty sections still have a slot
      // (the renderer reads from it without crashing).
      if (items.length === 0) {
        sectionMap[0] = makeEmptyItem();
      } else {
        items.forEach((item, itemIndex) => {
          // Auto-redact juvenile person fields in the NAMES section.
          const isJuvenileNameItem =
            section.key === 'names' && isJuvenileName(item);
          sectionMap[itemIndex] = isJuvenileNameItem
            ? makeJuvenileItem()
            : makeEmptyItem();
        });
      }
      redactionsMap[section.key] = sectionMap;
    } else {
      redactionsMap[section.key] = { 0: makeEmptyItem() };
    }
  }

  return redactionsMap;
};

export const resetCFSRedactions = initCFSRedactions;
